import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";
import HospitalNodeView from "./HospitalNodeView";

/**
 * TipTap 병원 노드 — ReferenceNode 동형(spec 설계 결정 5).
 *
 * 왜 필요한가: 스키마에 없는 span 속성은 ProseMirror가 파싱 시 버린다. 이 노드가
 * `span[data-hospital-alias]` 파싱·직렬화를 스키마에 등록해야 편집 저장(getHTML) 시 alias가
 * 살아남는다 — 없으면 첫 저장에서 병원 alias가 영구 소실되고 재변환이 불가능해진다.
 */
export interface HospitalOptions {
  HTMLAttributes: Record<string, any>;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    hospital: {
      setHospital: (attributes: { alias: string; text: string }) => ReturnType;
    };
  }
}

export const Hospital = Node.create<HospitalOptions>({
  name: "hospital",

  group: "inline",

  inline: true,

  atom: true,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      alias: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-hospital-alias"),
        renderHTML: (attributes) => {
          if (!attributes.alias) {
            return {};
          }
          return {
            "data-hospital-alias": attributes.alias,
          };
        },
      },
      text: {
        default: "",
        parseHTML: (element) => element.textContent,
        renderHTML: () => {
          return {};
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "span[data-hospital-alias]",
        getAttrs: (dom) => {
          const alias = (dom as HTMLElement).getAttribute("data-hospital-alias");
          const text = (dom as HTMLElement).textContent || "";
          return { alias, text };
        },
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        "data-hospital-alias": node.attrs.alias,
        "data-hospital-link": "true",
      }),
      node.attrs.text,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(HospitalNodeView);
  },

  addCommands() {
    return {
      setHospital:
        (attributes) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: attributes,
          });
        },
    };
  },
});
